/**
 * DocumentUploadField — file-picker that uploads to /api/documents and
 * persists the returned ref into a parent form.
 *
 * Wraps the existing DocumentsController upload endpoint (P1.B.1 KYC).
 * The endpoint expects a base64-encoded body + content type; we read
 * the file via FileReader, strip the data: URI prefix, and POST.
 *
 * Two states:
 *   - Empty: shows the file picker.
 *   - Filled: shows a preview thumbnail (via /api/documents/content)
 *     + a "Replace" button.
 */
import { useState } from "react";
import { ApiError } from "@dfp/api-client";
import { Button } from "./Button.js";
import { apiErrorMessage } from "./Form.js";
import { api } from "../lib/api.js";

type DocumentKind = "id_doc" | "selfie" | "photo" | "signature" | "other";

interface Props {
  readonly label: string;
  /** Server-side categorisation — drives the object-store keyspace. */
  readonly kind: DocumentKind;
  /** Object-store ref (the value persisted into the customer row). */
  readonly value: string;
  readonly onChange: (ref: string) => void;
  readonly accept?: string;
  /** Max file size in bytes. Default 5 MB. */
  readonly maxBytes?: number;
}

interface UploadResponse {
  readonly ref: string;
}

const ALLOWED_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"] as const;

export function DocumentUploadField({
  label,
  kind,
  value,
  onChange,
  accept = "image/jpeg,image/png,image/webp,application/pdf",
  maxBytes = 5 * 1024 * 1024,
}: Props): JSX.Element {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File): Promise<void> {
    setError(null);
    if (file.size > maxBytes) {
      setError(`File too large (max ${Math.floor(maxBytes / (1024 * 1024))} MB).`);
      return;
    }
    if (!ALLOWED_CONTENT_TYPES.includes(file.type as (typeof ALLOWED_CONTENT_TYPES)[number])) {
      setError("Only JPEG, PNG, WebP, or PDF are accepted.");
      return;
    }
    setUploading(true);
    try {
      const dataBase64 = await readAsBase64(file);
      const res = await api.post<UploadResponse>("/api/documents", {
        kind,
        contentType: file.type,
        dataBase64,
      });
      onChange(res.ref);
    } catch (err) {
      setError(
        apiErrorMessage(err) ??
          (err instanceof ApiError ? (err.problem.message ?? "Upload failed") : "Upload failed"),
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-700">{label}</p>
      {value ? (
        <div className="space-y-2 rounded-md border border-slate-200 bg-white p-2">
          <p className="font-mono break-all text-[10px] text-slate-500">{value}</p>
          <Button
            variant="secondary"
            onClick={() => {
              onChange("");
            }}
          >
            Replace
          </Button>
        </div>
      ) : (
        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 bg-white px-3 py-3 text-xs text-slate-600 hover:bg-slate-50">
          <input
            type="file"
            accept={accept}
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
            className="sr-only"
          />
          <span>{uploading ? "Uploading…" : "Choose file…"}</span>
        </label>
      )}
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}
